import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../../app.config';
import { getFrontlineMetricsColumnsConfig } from '../frontline/frontline-metric-column-config';
import { getFrontlineColumnConfigItemsConfig } from '../frontline/frontline-oa-column-config';
import { ColumnConfigItem, mapper } from '../models/coaching.global.model';
import {
    FLOAMetric,
    FLOAMetricTransformer,
    FLOAPerson,
    FLOATeamTransformer,
    FLOATransformer,
} from '../models/floa.model';
import { OaPeopleSort, SortDirection } from '../models/sorting.model';

@Injectable()
export class FrontlineOAService {
    _floa: BehaviorSubject<FLOAPerson> = new BehaviorSubject<FLOAPerson>(null);
    public _activeMember: BehaviorSubject<FLOAPerson> =
        new BehaviorSubject<FLOAPerson>(null);

    public _forceOpenTeamOA: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(null);

    public _rowExpanded: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(null);

    // _oaSortDirection: BehaviorSubject<SortDirection> = new BehaviorSubject<SortDirection>(null);
    // _expectedSortDirection: BehaviorSubject<SortDirection> = new BehaviorSubject<SortDirection>(SortDirection.DESC);

    _oaSortDirection: SortDirection;
    _expectedSortDirection: SortDirection;

    // key-value for an observable and primary key of metrics
    // metric icon state observables
    private _floaColumnConfig: BehaviorSubject<ColumnConfigItem[]>;
    private _metricColumnConfig: BehaviorSubject<ColumnConfigItem[]>;

    // current state of the Frontline OA component section
    private _cachedFloa: any;

    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
        private readonly _translateService: TranslateService,
    ) {}

    public hasPendingRefresh: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(false);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resetWidget() {
        this._floa.next(null);
        this._activeMember.next(null);
        if (this._floaColumnConfig) {
            this._floaColumnConfig.next(null);
        }
    }

    get floa(): Observable<FLOAPerson> {
        return this._floa.asObservable();
    }

    get oacolumns(): Observable<ColumnConfigItem[]> {
        return this._floaColumnConfig.asObservable();
    }

    get metricscolumns(): Observable<ColumnConfigItem[]> {
        return this._metricColumnConfig.asObservable();
    }

    get expected(): number {
        return 10;
    }

    get rowExpanded(): Observable<boolean> {
        return this._rowExpanded.asObservable();
    }

    get activeMember(): Observable<FLOAPerson> {
        return this._activeMember.asObservable();
    }

    get forceOpenTeamOA(): Observable<boolean> {
        return this._forceOpenTeamOA.asObservable();
    }

    get isTeamOAOpen(): boolean {
        return this._forceOpenTeamOA.getValue();
    }

    get isTeamOARowExpanded(): boolean {
        return this._rowExpanded.getValue();
    }

    get cachedFloa(): string {
        return this._cachedFloa;
    }

    getDirection(type: string): SortDirection {
        return type === 'oaSort'
            ? this._oaSortDirection
            : this._expectedSortDirection;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setRowExpanded(val: boolean) {
        this._rowExpanded.next(val);
    }

    getColumns(): ColumnConfigItem[] {
        return this._floaColumnConfig.getValue();
    }

    retrieveFLOA(
        userId: any,
        periodTypeId: any,
        currentDate: any,
        mapToGlobalServiceData = true,
    ): Observable<any> {
        const req1 = this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/coaching-insight/floa`,
                {
                    params: this.generateParams(
                        userId,
                        periodTypeId,
                        currentDate,
                    ),
                },
            )
            .pipe(map(mapper));

        const sub = req1.subscribe(
            (data: any[]) => {
                if (sub) sub.unsubscribe();
                this._mapResponse(data, mapToGlobalServiceData);
            },
            (error: Error) => {
                if (sub) sub.unsubscribe();
                this._mapResponse(error);
            },
        );
        return req1;
    }

    retrieveTeamFLOA(
        userId: any,
        periodTypeId: any,
        currentDate: any,
    ): Observable<any> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/coaching-insight/subfloa`,
                {
                    params: this.generateParams(
                        userId,
                        periodTypeId,
                        currentDate,
                    ),
                },
            )
            .pipe(
                map(mapper),
                map((data) => this._mapTeamResponse(data)),
            );
    }

    retrieveFLOAMetrics(
        userId: any,
        periodTypeId: any,
        currentDate: any,
        mapToGlobalServiceData = true,
    ): Observable<any> {
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.COACHING}/coaching-insight/metricfloa`,
                {
                    params: this.generateParams(
                        userId,
                        periodTypeId,
                        currentDate,
                    ),
                },
            )
            .pipe(
                map(mapper),
                map((data) => this._mapMetricResponse(data)),
            );
    }

    setForceOpenTeam(state: boolean): void {
        this._forceOpenTeamOA.next(state);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    setCurrentFloaState(f: FLOAPerson) {
        if (f) this._floa.next(f);
    }

    private _mapResponse(data: any, mapToGlobalServiceData = true): void {
        const floa = new FLOATransformer().transform(data);

        if (!floa || floa.userId == null) {
            this._floa.next(null);
            return;
        }

        if (!floa) return;

        if (mapToGlobalServiceData) {
            this._floa.next(floa);
        }
    }

    private _mapTeamResponse(data: any): FLOAPerson[] {
        const leader: FLOAPerson = this._floa.getValue(); // there is only 1 leader per FLOA view
        const floa = new FLOATeamTransformer(leader).transform(data);

        // use the current sort, but don't change direction
        if (
            (this._oaSortDirection === SortDirection.DEFAULT &&
                this._expectedSortDirection === SortDirection.DEFAULT) ||
            (this._expectedSortDirection !== SortDirection.DEFAULT &&
                this._oaSortDirection === SortDirection.DEFAULT)
        ) {
            floa.sort(
                OaPeopleSort.sortFLOAByRequirements(
                    this._expectedSortDirection,
                ),
            );
        } else if (
            this._oaSortDirection !== SortDirection.DEFAULT &&
            this._expectedSortDirection === SortDirection.DEFAULT
        ) {
            floa.sort(OaPeopleSort.sortByFLOAScore(this._oaSortDirection));
        }

        if (!floa) return null;

        return floa;
    }

    private _mapMetricResponse(data: any): FLOAMetric[] {
        const metrics = new FLOAMetricTransformer().transform(data);

        if (!metrics) return null;

        return metrics;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    initializeColumnConfig() {
        this._floaColumnConfig = new BehaviorSubject<ColumnConfigItem[]>(
            getFrontlineColumnConfigItemsConfig(this._translateService),
        );
        this._metricColumnConfig = new BehaviorSubject<ColumnConfigItem[]>(
            getFrontlineMetricsColumnsConfig(this._translateService),
        );
        this._oaSortDirection = SortDirection.DESC;
        this._expectedSortDirection = SortDirection.DEFAULT;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    traverse(type: string, dir: SortDirection, subordinates: FLOAPerson[]) {
        if (!subordinates || !subordinates.length) return;

        const expandedsubs = subordinates.filter(
            (f) => f.isExpanded && f.subOrdinates && f.subOrdinates.length,
        );
        if (expandedsubs.length) {
            expandedsubs.forEach((q) => {
                this.traverse(type, dir, q.subOrdinates);
            });
        } else {
            if (type === 'oascore') {
                subordinates.sort(OaPeopleSort.sortFLOAByRequirements(dir)); // default Sort for all
            } else if (type === 'expected') {
                if (dir === SortDirection.DEFAULT) {
                    // this.sortExpected(SortDirection.DESC);
                    subordinates.sort(
                        OaPeopleSort.sortFLOAByRequirements(SortDirection.DESC),
                    ); // default Sort for all
                } else {
                    subordinates.sort(OaPeopleSort.sortByExpectedOpps(dir));
                }
            }
        }
    }

    sortExpected(forceDirection?: SortDirection): SortDirection {
        if (this._expectedSortDirection === SortDirection.DESC) {
            this._expectedSortDirection = SortDirection.ASC;
            this._oaSortDirection = SortDirection.DEFAULT;
        } else if (this._expectedSortDirection === SortDirection.ASC) {
            this._expectedSortDirection = SortDirection.DEFAULT;
            this._oaSortDirection = SortDirection.DESC;
        } else if (this._expectedSortDirection === SortDirection.DEFAULT) {
            this._expectedSortDirection = SortDirection.DESC;
            this._oaSortDirection = SortDirection.DEFAULT;
        }

        if (forceDirection != null) {
            this._expectedSortDirection = forceDirection;
        }

        return this._expectedSortDirection;
    }

    sortFLOA(forceDirection?: SortDirection): SortDirection {
        this._oaSortDirection =
            this._oaSortDirection === SortDirection.DESC
                ? SortDirection.ASC
                : SortDirection.DESC;

        this._expectedSortDirection = SortDirection.DEFAULT;

        if (forceDirection != null) {
            this._oaSortDirection = forceDirection;
        }

        return this._oaSortDirection;
    }

    sortTeamBy(
        type: string,
        force?: SortDirection,
        floa?: FLOAPerson,
    ): SortDirection {
        if (floa == null) {
            floa = this._floa.getValue();
        }

        const dir =
            type === 'oascore'
                ? this.sortFLOA(force)
                : this.sortExpected(force);

        this.traverse(type, dir, floa.subOrdinates);

        return dir;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    saveCurrentViewState() {
        const floa = this._floa.getValue();
        this._cachedFloa = JSON.stringify(floa);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    restoreViewState() {
        if (typeof this._cachedFloa === 'string') {
            const x: FLOAPerson = JSON.parse(this._cachedFloa);
            this._floa.next(x);
            this._cachedFloa = null;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resetCachedFLOA() {
        this._cachedFloa = null;
    }

    generateParams(
        userId: any,
        periodTypeId: any,
        currentDate: Date,
    ): HttpParams {
        const params = new HttpParams()
            .set('performerUserId', userId.toString())
            .set('periodTypeId', periodTypeId.toString());
        if (currentDate) {
            params.set(
                'currentDate',
                typeof currentDate === 'string'
                    ? currentDate
                    : currentDate.toJSON(),
            );
        }
        return params;
    }
}
